import { html, LitElement, nothing, PropertyValues, TemplateResult } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { BehaviorProfileListItem, BehaviorProfileStatus } from '../../types';
import { repeat } from 'lit/directives/repeat.js';
import { LoadMoreOnScrollController } from '../../../../ui/reactiveControllers/LoadMoreOnScrollController';
import { LOAD_LIMIT } from '../../../phasrBehavioralProfilesComponent/types';

/**
 * @summary Type here the description of this component
 * @tag gz-phasr-restricted-profiles-list
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('gz-phasr-restricted-profiles-list')
export class PhasrRestrictedProfilesListWebComponent extends LitElement {
  @property({ type: Array, attribute: true, reflect: true })
  items: BehaviorProfileListItem[] = [];

  @property({ type: String })
  listTitle = '';

  @property({ type: String })
  apiType = '';

  /**
   * The ID of the active item. This is used to determine which item is selected in the list.
   */
  @property({ type: String })
  activeItemId = '';

  /**
   * The ID of the active collection. This is used to determine which collection is loaded in the list.
   */
  @property({
    type: String,
    reflect: true,
    hasChanged: (newVal: any, oldVal: any) => true,
  })
  activeCollectionId = '';

  @property({ type: Boolean })
  hasMoreItems = false;

  @property({ type: Boolean })
  isLoading = false;

  /**
   * The disabled state of the component. If true, all the checkboxes in the list will be disabled.
   */
  @property({ type: Boolean })
  disabled = false;

  /**
   * The message to display below the empty state image when the list is empty.
   * If not provided, the empty state will not be displayed.
   */
  @property({ type: String })
  emptyStateMessage: string;

  @query('#sentinel')
  private sentinel!: HTMLDivElement;

  @query('ul')
  private intersectionRoot!: HTMLDivElement;

  private loadMoreController: LoadMoreOnScrollController = new LoadMoreOnScrollController(this, {
    onLoadMore: () => this.onLoadMore(),
    observerOptions: {
      root: this.intersectionRoot,
      rootMargin: '10px',
      threshold: 0.1,
    },
    getSentinelElement: () => this.sentinel,
  });

  loadMoreItems?: (collectionId: string) => Promise<BehaviorProfileListItem[]>;

  protected updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);

    if (changedProperties.has('activeCollectionId')) {
      // Manually disconnect and re-observe the sentinel when the activeCollectionId is updated.
      if (this.loadMoreController) {
        this.loadMoreController.hostDisconnected();
        this.loadMoreController.hostUpdated();
      }
    }
  }

  static get styles() {
    return styles;
  }

  private async onLoadMore(): Promise<void> {
    // If we're already loading, skip
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;

    if (this.loadMoreItems) {
      const newItems = await this.loadMoreItems(this.activeCollectionId);
      if (newItems.length < LOAD_LIMIT) {
        // No more items => remove sentinel, which will stop the controller from firing
        this.hasMoreItems = false;
      }

      // Done fetching
      this.isLoading = false;
    }
  }

  private onCheckboxChange(e: CustomEvent, item: BehaviorProfileListItem) {
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent('change', {
        detail: {
          ...e.detail,
          apiType: this.apiType,
          parentId: item.parentId,
          departmentId: item.departmentId,
        },
      })
    );
  }

  private onListItemClick(e: MouseEvent, item: BehaviorProfileListItem) {
    if (e.composedPath().find((elem: HTMLElement) => elem.tagName === 'GZ-CHECKBOX')) {
      return;
    }

    if (item.totalProfiles) {
      this.activeItemId = item.id;
    }

    this.dispatchEvent(
      new CustomEvent('select', {
        detail: {
          apiType: this.apiType,
          id: item.id,
          parentId: item.parentId,
        },
      })
    );
  }

  private renderEmptyState(): TemplateResult | typeof nothing {
    if (!this.emptyStateMessage && this.isLoading) {
      return html`<gz-progress-spinner indeterminate spinnerWidth="medium"></gz-progress-spinner>`;
    }
    if (!this.emptyStateMessage && !this.isLoading) {
      return nothing;
    }
    return html`
      <div class="empty-state">
        <gz-image-repo state="empty">
          <gz-text fontSize="18" fontWeight="500"> ${this.emptyStateMessage} </gz-text>
        </gz-image-repo>
      </div>
    `;
  }

  private renderList(): TemplateResult {
    return html`
      <ul class="list-unstyled">
        ${repeat(
          this.items,
          (item: BehaviorProfileListItem) => item.id,
          item => {
            const isChecked = item.status === BehaviorProfileStatus.SELECTED;
            const indeterminate = item.status === BehaviorProfileStatus.PARTIAL;
            return html`
              <li
                @click="${e => this.onListItemClick(e, item)}"
                id="${item.id}"
                class="${item.id === this.activeItemId ? 'active' : ''}"
              >
                <gz-checkbox
                  .value="${item.id}"
                  ?checked="${isChecked}"
                  ?indeterminate="${indeterminate}"
                  ?disabled="${this.disabled}"
                  @change="${e => this.onCheckboxChange(e, item)}"
                ></gz-checkbox>
                <gz-tooltip isEllipsis .tooltipText="${item.name}">
                  <span class="item-name">${item.name}</span>
                </gz-tooltip>
              </li>
            `;
          }
        )}
      </ul>

      ${this.hasMoreItems
        ? html`
            <div id="sentinel" class="scroll-guard">
              <!-- Show spinner if loading -->
              ${this.isLoading
                ? html`<gz-progress-spinner indeterminate spinnerWidth="medium"></gz-progress-spinner>`
                : nothing}
            </div>
          `
        : nothing}
    `;
  }

  render() {
    return html`
      <div class="container">
        <gz-title class="list-title" fontWeight="600" fontSize="11">${this.listTitle.toUpperCase()}</gz-title>
        <div class="content">${this.items.length > 0 ? this.renderList() : this.renderEmptyState()}</div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-phasr-restricted-profiles-list': PhasrRestrictedProfilesListWebComponent;
  }
}
